/**
 * Quick-Move paneli - DENEYSEL gozlem katmani.
 *
 * ONEMLI: bu panel AL/SAT SINYALI DEGILDIR. Mevcut UT Bot/DI sinyal
 * akisindan (SignalScreen) TAMAMEN AYRIDIR - hicbir emir acmaz, mevcut
 * hicbir ekrani etkilemez. Sadece "su an kisa vadede hareketlenme
 * olasiligi yuksek gorunen semboller" seklinde ayri, acikca etiketlenmis
 * bir siralama gosterir (bkz. backend scanner/quickmove.py).
 *
 * Kendi tablo/rozet gorunumunu ICAT ETMEZ: Tarama sekmesiyle
 * AYNI satir/kart bilesenlerini (`.signal`, CoinAvatar, Pill, status-line)
 * yeniden kullanir - sekmeler arasi gecince gorsel kalite sirittigi icin
 * eklendi (bkz. kullanicinin geri bildirimi).
 */
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../api/client";
import type {
  QuickMoveHistoryRow,
  QuickMoveIgnition,
  QuickMoveRow,
  QuickMoveScan,
} from "../api/types";
import { ago, dateTime, hourMinute, pct, price } from "../lib/format";
import { CoinAvatar, Empty, IconRefresh, Pill } from "./Ui";

/** Sunucu mum kapaninca ~2 sn icinde hesapliyor (anlik akis); ekran da sik yenilenir. */
const POLL_MS = 5_000;

/** Baslayanlar: son baslangici bu kadar saat icinde olan coinler (6 -> 24 -> 12,
 *  kullanici istegi: gecmis Baslayanlar uzerinden izleniyor). */
const STARTS_WINDOW_H = 12;
/** Isinma: Saatin uyanani gibi coin basina tek satir, 24 saat kalir (kullanici 10.10). */
const WARM_WINDOW_H = 24;
/** Soldaki cizgi: ilk isinmadan beri bu kadar oynamadiysa renksiz ("yerinde"). */
const WARM_FLAT_PCT = 0.5;

/** 24 saatte bundan fazla yukselmis coinler Hizli Hareket listelerinde
 *  gosterilmez (kullanici istegi). Olcum (120 gun): bu coinlerin %67'si
 *  sonraki 24 saatte -%15 goruyor (digerlerinde %13). */
const MAX_24H_RISE_PCT = 40;

/** Isinma "once hacim" rozeti (kullanici 09.10; research/warm_wake.py, 7 ay): baslangictan
 *  ONCEKI 4 saatte hacim normalinin bu kati ve ustu -> 4'te 1'i gunun en cok yukselen 10'u
 *  (Isinma ortalamasi 7'de 1). Iki yone de sert oynar (+%10 %35, -%10 %27). */
const PRE_VOLUME_BADGE_X = 4;

/** Tek rozet (kullanici 10.10: "hacim, hareketlilik, diger coinlerden ayristiran ne varsa tek
 *  isaretle anlasam"). Isinan coinin 5 isaretten kacini tasidigi; research/warm_badge.py (215 gun,
 *  iki yarida ayni): isaret sayisina gore gunu en cok yukselen 10'da bitirme
 *  0: %13, 1: %22, 2: %33, 3: %43, 4+: %55+. Her seviyede 4 saat sonrasi medyan eksi. */
type Sign = { label: string; on: boolean; detail: string };
const SIGN_ODDS = [
  "8'de 1'i",
  "5'te 1'i",
  "3'te 1'i",
  "10'da 4'ü",
  "yarıdan fazlası",
  "yarıdan fazlası",
];

function warmSigns(
  base: QuickMoveIgnition,
  history: QuickMoveIgnition[],
): Sign[] {
  const volume = base.pre_volume_x ?? null;
  const at = (ms: number) => ` (${hourMinute(ms)})`;
  return [
    {
      label: "Hacim",
      on: volume != null && volume >= PRE_VOLUME_BADGE_X,
      detail:
        volume != null && volume >= PRE_VOLUME_BADGE_X
          ? `Yükselmeden önce ${Math.round(volume)} kat fazla alım-satım oldu`
          : "Yükselmeden önce alım-satım artmadı",
    },
    {
      label: "Hızlandı",
      on: base.fast_at != null,
      detail:
        base.fast_at != null
          ? `İlk 1 saatte %5'ten fazla çıktı${at(base.fast_at)}`
          : "İlk 1 saatte %5 çıkamadı",
    },
    {
      label: "Tekrar",
      on: history.length >= 2,
      detail:
        history.length >= 2
          ? `Bugün ${history.length} kez yükselişe geçti`
          : "Bugün sadece 1 kez yükselişe geçti",
    },
    {
      label: "Lider",
      on: base.leader_at != null,
      detail:
        base.leader_at != null
          ? `Bir ara piyasanın en hareketli coini oldu${at(base.leader_at)}`
          : "En hareketli coin olmadı",
    },
    {
      label: "Çok sert",
      on: base.sharp_at != null,
      detail:
        base.sharp_at != null
          ? `Birkaç dakikada çok sert fırladı${at(base.sharp_at)}`
          : "Sert bir fırlama yapmadı",
    },
  ];
}

/** Listede dogrudan gorunen en az nokta (kullanici 10.10: "cok coin var"). research/warm_badge.py:
 *  2+ nokta gunde ~21 coin (75 yerine), gunun en cok yukselenlerinin 10'da 7'si buraya ulasiyor;
 *  0-1 noktalilar listenin altinda "N coin daha" ile acilir. */
const SHOW_MIN_SIGNS = 2;
/** Sessiz kalan satirlar (kullanici 11.10: "pump yapacak coinleri digerlerinden ayristirmak").
 *  research/warm_stale.py (215 gun): 2+ noktali coinin son hareketinden (yeni baslangic veya yeni
 *  nokta) beri gecen sureye gore yildiz olma: <1 s %36, 1-2 s %30, 2-4 s %21, 4-8 s %12, 8 s+ %7
 *  (Isinma ortalamasi %13). 4 s+ soluk, 8 s+ "N coin daha" altina. Yeni hareket gelince geri doner. */
const FADE_QUIET_MS = 4 * 3_600_000;
const HIDE_QUIET_MS = 8 * 3_600_000;

function SignBadge({ signs }: { signs: Sign[] }) {
  const n = signs.filter((s) => s.on).length;
  if (n === 0) return null;
  const tone =
    n >= 4 ? "down" : n === 3 ? "warn" : n === 2 ? "info" : "neutral";
  const which = signs
    .filter((s) => s.on)
    .map((s) => s.label)
    .join(", ");
  return (
    <Pill
      tone={tone}
      title={`5 işaretten ${n}: ${which}. Ölçüm (7 ay): böyle coinlerin ${SIGN_ODDS[n]} günü en çok yükselen 10 coin arasında bitirdi. Al sinyali değil.`}
    >
      <span className="qm-signs" aria-label={`5 işaretten ${n}`}>
        {signs.map((s, i) => (
          <i key={s.label} data-on={i < n ? "" : undefined} />
        ))}
      </span>
    </Pill>
  );
}

function risenTooMuch(row: { change_24h_pct?: number | null }): boolean {
  return row.change_24h_pct != null && row.change_24h_pct >= MAX_24H_RISE_PCT;
}

/** Gecmis satirlar (Baslayanlar): kural TETIK ANINDAKI 24s getiriye
 *  bakar. Su anki degisime bakmak devam eden pump'i siliyordu (canli: ORCA
 *  +%34'te yakalandi, sonra +%45 olunca listeden kayboldu). */
function risenAtTrigger(row: {
  trigger_24h_pct?: number | null;
  change_24h_pct?: number | null;
}): boolean {
  const v = row.trigger_24h_pct ?? row.change_24h_pct;
  return v != null && v >= MAX_24H_RISE_PCT;
}

/** Baslayanlar: gunun 2.-4. baslangici; +%40 GIZLENMEZ, kirmizi rozetle
 *  isaretlenir (kullanici, 07.10). 1. baslangic "Isınma (BETA)"da.
 *  6.+ baslangic GOSTERILMEZ: coin 6. kez baslayinca listeden CIKAR (kullanici,
 *  08.10: "5. kalacak ama 5'ten sonra ekranda cikmasin"). Olcum
 *  (research/nth_check.py, 40 gun): "al, +%15'te sat, yoksa 48 s sonra sat" net
 *  ort. 2. +%1,5, 3. +%1,1, 4. +%0,2, 5. -%1,5; 6. ve sonrasi her sirada eksi.
 *  +%40: 4 saatte +%10 tepe %44 ama %71'i once -%5. */
const MANY_STARTS_FROM = 5;
/** Listede gosterilen en son baslangic sirasi. */
const MAX_SHOWN_START = 5;

/** Baslayanlar gunun 2.-5. baslangicini gosterir (bkz. MAX_SHOWN_START). */
function inStartZone(row: QuickMoveIgnition): boolean {
  const nth = row.nth_24h ?? 1;
  return nth >= 2 && nth <= MAX_SHOWN_START;
}

/** Hizli Hareket alt gorunumleri (ust sekmeler). */
/** En guclu 10 alt siniri (kullanici 09.10; Saatin uyanani ile ayni esik). */
const TOP_MIN_SCORE = 3;
type QmView = "top" | "starts";

/** Zaman sirasi: listeler ve acilan gecmisler (kullanici: "iki tarafa da
 *  yeniden eskiye butonu"). YALNIZCA Baslayanlar - En guclu 10'da mantiksizdi. */
type TimeOrder = "new" | "warm";

const HISTORY_POLL_MS = 15_000;

/** Evren USDT-kotasyonlu spot pariteleridir (bkz. CLAUDE.md Kural 1) -
 *  taban varligi turetmek icin ek bir API cagrisi gerekmez. */
function baseAsset(symbol: string): string {
  return symbol.endsWith("USDT") ? symbol.slice(0, -4) : symbol;
}

export function QuickMovePanel({
  onSelect,
}: {
  onSelect: (symbol: string) => void;
}) {
  const [scan, setScan] = useState<QuickMoveScan | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<QuickMoveHistoryRow[] | null>(null);
  const [ignitions, setIgnitions] = useState<QuickMoveIgnition[] | null>(null);
  const [historyHours, setHistoryHours] = useState(48);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await api.quickMove();
      setScan(result);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const result = await api.quickMoveHistory();
      setHistory(result.rows);
      setIgnitions(result.ignitions ?? []);
      setHistoryHours(result.hours ?? 48);
    } catch {
      // Gecmis ikincil bir gorunum - hata anlik taramayi bozmasin, sessizce atlanir.
    }
  }, []);

  useEffect(() => {
    void load();
    void loadHistory();
    // Diger sekmelerle (Tarama) TUTARLI: sekme gizliyken
    // yoklama durur - 80 sembollu paralel REST taramasini arka planda
    // gereksiz yere calistirmamak icin (bkz. App.tsx'teki ayni desen).
    const id = setInterval(() => {
      if (document.visibilityState === "visible") void load();
    }, POLL_MS);
    const historyId = setInterval(() => {
      if (document.visibilityState === "visible") void loadHistory();
    }, HISTORY_POLL_MS);
    // Ekrana geri donulunce HEMEN yenile (kullanici 11.10: "bazen F5 yapinca geliyor yeni
    // bilgiler"). Arka planda yoklama durur; iPhone ana ekran uygulamasi zamanlayicilari da
    // dondurur - donuste siradaki turu beklemek eski listeyi gosteriyordu.
    const refresh = () => {
      if (document.visibilityState !== "visible") return;
      void load();
      void loadHistory();
    };
    document.addEventListener("visibilitychange", refresh);
    window.addEventListener("focus", refresh);
    window.addEventListener("pageshow", refresh);
    window.addEventListener("online", refresh);
    return () => {
      clearInterval(id);
      clearInterval(historyId);
      document.removeEventListener("visibilitychange", refresh);
      window.removeEventListener("focus", refresh);
      window.removeEventListener("pageshow", refresh);
      window.removeEventListener("online", refresh);
    };
  }, [load, loadHistory]);

  const allRows = scan?.top ?? [];
  // En guclu 10 CANLI liste: +%40 coin GIZLENMEZ, isaretlenir (kullanici:
  // "ORCA nerede" - gizleme devam eden pump'i sakliyordu).
  const rows = allRows;

  // Canli satirda "Gecmisi var" rozeti icin - hangi semboller en az bir kez
  // guclu esigi gecmis (bkz. kullanici istegi: ayni coin canli listede
  // tekrar tekrar cikinca, gecmisiyle iliskilendirilebilsin).
  const historyBySymbol = useMemo(() => {
    const map = new Map<string, QuickMoveHistoryRow[]>();
    for (const row of history ?? []) {
      const list = map.get(row.symbol);
      if (list) list.push(row);
      else map.set(row.symbol, [row]);
    }
    for (const list of map.values())
      list.sort((a, b) => b.close_time - a.close_time);
    return map;
  }, [history]);
  // En guclu 10'da Baslayanlar'daki nokta rozeti (kullanici 11.10) - ayni hesap.
  const signsBySymbol = useMemo(() => {
    const map = new Map<string, Sign[]>();
    const nth = withDayNth(ignitions);
    for (const g of nth ? warmGroups(nth) : []) {
      if (g.first) map.set(g.first.symbol, warmSigns(g.first, g.history));
    }
    return map;
  }, [ignitions]);
  // Kullanici (09.10): En guclu 10 esigi 6,5'ten 3'e indi ("Guclu" rozeti 6,5'te kalir).
  const topRows = rows.filter((r) => r.score >= TOP_MIN_SCORE).slice(0, 10);

  const [view, setViewState] = useState<QmView>(() => {
    try {
      const saved = window.localStorage.getItem("qm.view");
      // "Saatin uyanani" sekmesi kaldirildi (kullanici 10.10) -> "top".
      return saved === "starts" ? saved : "top";
    } catch {
      return "top";
    }
  });
  const setView = useCallback((next: QmView) => {
    setViewState(next);
    try {
      window.localStorage.setItem("qm.view", next);
    } catch {
      // depolama kapali - secim yalnizca bu oturumda
    }
  }, []);

  // Baslayanlar TEK liste (kullanici 10.10: Isinma artik tum baslangiclari coin basina tek
  // satirda gosteriyor; "Yeniden eskiye" onun alt kumesiydi ve kaldirildi).
  const order: TimeOrder = "warm";

  const views: [QmView, string][] = [
    ["top", "En güçlü 10"],
    ["starts", "Başlayanlar"],
  ];

  return (
    <>
      <div className="title-row">
        <h2 className="section-title">Hızlı Hareket</h2>
        <button
          type="button"
          className="icon-btn"
          onClick={() => void load()}
          aria-label="Yenile"
          disabled={loading}
        >
          <IconRefresh spinning={loading} />
        </button>
      </div>

      <div className="status-line">
        <span className="live" data-state={scan?.available ? "live" : "idle"}>
          <span className="pulse" />
          {scan?.available ? "Canlı tarama" : "Başlıyor"}
        </span>
        {scan ? (
          <span>{scan.scanned_count || scan.symbol_count} coin tarandı</span>
        ) : null}
        {scan &&
        scan.scanned_count > 0 &&
        (scan.streamed_count ?? 0) >= scan.scanned_count * 0.8 ? (
          <span title="1 dakikalık mumlar Binance canlı akışından geliyor: mum kapanınca 1–3 saniyede hesaplanır">
            anlık akış
          </span>
        ) : null}
        {scan?.generated_at ? (
          <span>son güncelleme {ago(scan.generated_at)}</span>
        ) : null}
      </div>

      {/* Kullanici: "asiri karisik, eskiden 10 coin oluyordu". Varsayilan
          gorunum yine tek liste; digerleri sekmede, acilmadikca gorunmez. */}
      <div
        className="segmented qm-views"
        role="tablist"
        aria-label="Hızlı Hareket görünümü"
      >
        {views.map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            className="segment"
            aria-pressed={view === key}
            aria-selected={view === key}
            onClick={() => setView(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {view === "top" ? (
        <>
          <details className="legend-details">
            <summary>Nasıl okunur?</summary>
            <ul className="howto-list">
              <li>
                <strong>Ne gösterir?</strong> Bütün coinler içinde{" "}
                <strong>şu an fiyatı en hızlı oynayan 10 coin.</strong> Yalnızca
                skoru 3 ve üstü olanlar gösterilir; o yüzden liste 10'dan kısa
                ya da boş olabilir.
              </li>
              <li>
                <strong>Skor ne?</strong> Fiyatın son birkaç dakikada ne kadar
                hızlı gittiği ve hızlanıp hızlanmadığı. Skor büyükse coin hızlı
                oynuyor demektir.
              </li>
              <li>
                <strong>Mavi "Güçlü" (skor 6,5 ve üstü):</strong> 10 coinden 9'u
                sonraki 1 saatte en az %3 oynuyor (rastgele bir coinde 25'te 1).
                Ama yarısı yukarı, yarısı aşağı: 1 saat sonra fiyat genelde
                biraz düşmüş oluyor.{" "}
                <strong>Turuncu "Çok güçlü" (skor 10 ve üstü):</strong> 20'de
                19.
              </li>
              <li>
                <strong>Dikkat: yön söylemez.</strong> "Oynuyor" demek
                "yükseliyor" demek değil; hızla düşen coin de listenin başına
                çıkar. Yükselen coinler için Başlayanlar'a bak.
              </li>
              <li>
                <strong>Noktalar ●●○○○:</strong> coin bugün Başlayanlar'a
                düştüyse oradaki nokta rozeti burada da görünür (anlamı
                Başlayanlar → Nasıl okunur?).
              </li>
              <li>
                <strong>Satırın altı:</strong> sıra, fiyat ve "14 kez": skor son
                2 günde kaç kez 6,5'i geçti. 6 ve üstü artık işe yaramıyor: 5'te
                1'i %15 düştü. <strong>Sağdaki yüzde:</strong> son 3 dakikadaki
                fiyat değişimi.
              </li>
              <li>
                <strong>Turuncu "24s +%…":</strong> coin son 24 saatte %40'tan
                fazla yükselmiş. Bunların 3'te 2'si sonraki 24 saatte %15 düştü.
              </li>
              <li>
                Satıra basınca o coinin son {historyHours} saatteki hareketleri
                açılır. Coin adına basınca detay ekranı açılır.
              </li>
            </ul>
          </details>
          {error ? (
            <Empty title="Hızlı Hareket verisi alınamadı" detail={error} />
          ) : !scan?.available ? (
            <div className="signal-list">
              {[0, 1, 2].map((index) => (
                <div key={index} className="skeleton" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <Empty
              title="Şu an liste boş"
              detail="Tarama birazdan yenilenecek."
            />
          ) : topRows.length === 0 ? (
            <Empty
              title="Şu an skoru 3'ü geçen coin yok"
              detail="Piyasa sakin. Skoru 3'ü geçen coin olunca burada görünür."
            />
          ) : (
            <>
              <div className="table-shell" data-kind="quickmove">
                <div className="signal-head" aria-hidden="true">
                  <span />
                  <span>Sembol</span>
                  <span>Hız</span>
                  <span>İvme</span>
                  <span className="align-end">Skor</span>
                </div>
                <div className="signal-list">
                  {topRows.map((row, index) => (
                    <QuickMoveItem
                      key={row.symbol}
                      row={row}
                      rank={index + 1}
                      onSelect={onSelect}
                      triggers={historyBySymbol.get(row.symbol) ?? []}
                      signs={signsBySymbol.get(row.symbol)}
                    />
                  ))}
                </div>
              </div>
            </>
          )}
        </>
      ) : null}

      {view === "starts" ? (
        <IgnitionSection
          rows={ignitions}
          hours={historyHours}
          onSelect={onSelect}
          order={order}
        />
      ) : null}
    </>
  );
}

/** Hareket baslangiclari - TUM evren, 24 saatte ilk atesleme (bkz. backend
 *  scanner/quickmove.py ATESLEME). Sayilar arastirmadan (120 gun, 7.988 olay;
 *  research/REPORT_sinyalx_prepump.md), canli olcum degil. */
/** Turkiye takvim gunu (UTC+3, 00:00'da degisir). */
const TR_OFFSET_MS = 3 * 3_600_000;
const trDay = (ms: number) => Math.floor((ms + TR_OFFSET_MS) / 86_400_000);

/** Sira numarasini GOSTERILEN kayitlardan, gun icinde 1-2-3... olarak yeniden
 *  hesaplar (kullanici, 08.10: eski kayitlarda kayan 24 saat sayimi 1-2-4-4-5-6-6
 *  gorunuyordu). Sunucu da artik gune gore sayiyor; bu, eski satirlari duzeltir. */
type WarmGroup = {
  last: QuickMoveIgnition;
  history: QuickMoveIgnition[];
  first: QuickMoveIgnition | undefined;
  dots: number | undefined;
  moved: number;
};

/** Isinma (kullanici 10.10: "Saatin uyanani gibi"): son 24 saatte ILK kez isinan her coin
 *  TEK grup; sonraki baslangiclarda dusmez, en uste cikar. `rows` withDayNth'ten gecmis olmali.
 *  Baslayanlar listesi ve En guclu 10'daki nokta rozeti (kullanici 11.10) ayni hesabi kullanir. */
function warmGroups(rows: QuickMoveIgnition[]): WarmGroup[] {
  const since = Date.now() - WARM_WINDOW_H * 3_600_000;
  const firsts = new Map<string, QuickMoveIgnition>();
  for (const row of rows) {
    if (row.close_time < since || (row.nth_24h ?? 1) !== 1) continue;
    const cur = firsts.get(row.symbol);
    if (!cur || row.close_time > cur.close_time) firsts.set(row.symbol, row);
  }
  return [...firsts.values()]
    .map((first) => {
      const after = rows
        .filter(
          (r) => r.symbol === first.symbol && r.close_time >= first.close_time,
        )
        .sort((a, b) => a.close_time - b.close_time);
      const last = after[after.length - 1] ?? first;
      const dots = warmSigns(first, after).filter((x) => x.on).length;
      // Yeni nokta alan (hizlandi / sert / lider) da en uste cikar - yeni baslangic gibi.
      const moved = Math.max(
        last.close_time,
        first.fast_at ?? 0,
        first.sharp_at ?? 0,
        first.leader_at ?? 0,
      );
      return { last, history: after, first, dots, moved };
    })
    .sort((a, b) => b.moved - a.moved);
}

function withDayNth(
  rows: QuickMoveIgnition[] | null,
): QuickMoveIgnition[] | null {
  if (!rows) return null;
  const seen = new Map<string, number>();
  return [...rows]
    .sort((a, b) => a.close_time - b.close_time)
    .map((row) => {
      const key = `${row.symbol}:${trDay(row.close_time)}`;
      const nth = (seen.get(key) ?? 0) + 1;
      seen.set(key, nth);
      return { ...row, nth_24h: nth };
    });
}

function IgnitionSection({
  rows: rawRows,
  onSelect,
  order,
}: {
  rows: QuickMoveIgnition[] | null;
  hours: number;
  onSelect: (symbol: string) => void;
  order: TimeOrder;
}) {
  const rows = useMemo(() => withDayNth(rawRows), [rawRows]);
  // Kullanici: "neden 57 tane" - yalnizca SON 6 SAAT, en yeni ustte.
  // "Tetige cok asiri coin dusuyor": yalnizca gunun 2.-4. baslangici.
  // Coin basina TEK satir = SON baslangic (fiyat + o andan beri degisim);
  // satira basinca onceki baslangiclar ve her birinin bir sonrakine kadar
  // yaptigi hareket (kullanici istegi).
  const visible = useMemo(() => {
    if (!rows) return null;
    if (order === "warm") return warmGroups(rows);
    const since = Date.now() - STARTS_WINDOW_H * 3_600_000;
    const latest = new Map<string, QuickMoveIgnition>();
    for (const row of rows) {
      if (row.close_time < since) continue;
      const cur = latest.get(row.symbol);
      if (!cur || row.close_time > cur.close_time) latest.set(row.symbol, row);
    }
    return [...latest.values()]
      .filter((last) => inStartZone(last))
      .sort((a, b) => b.close_time - a.close_time)
      .map((last) => ({
        last,
        // Bu coinin elimizdeki TUM baslangiclari (gizlenen 1. dahil), eskiden yeniye.
        history: rows
          .filter(
            (r) => r.symbol === last.symbol && r.close_time <= last.close_time,
          )
          .sort((a, b) => a.close_time - b.close_time),
        first: undefined as QuickMoveIgnition | undefined,
        dots: undefined as number | undefined,
        moved: last.close_time,
      }));
  }, [rows, order]);
  const [showAll, setShowAll] = useState(false);
  const now = Date.now();
  const shown =
    visible === null || showAll
      ? visible
      : visible.filter(
          (v) =>
            (v.dots ?? SHOW_MIN_SIGNS) >= SHOW_MIN_SIGNS &&
            (v.first === undefined || now - v.moved < HIDE_QUIET_MS),
        );
  const hiddenCount =
    visible === null ? 0 : visible.length - (shown?.length ?? 0);
  return (
    <>
      <details className="legend-details">
        <summary>Nasıl okunur?</summary>
        <ul className="howto-list">
          <li>
            <strong>Ne gösterir?</strong> Son 24 saatte{" "}
            <strong>ilk kez ısınan</strong> (yükselmeye başlayan) coinler. Her
            coin <strong>tek satır</strong>.
          </li>
          <li>
            <strong>"Başlangıç" ne?</strong> Coinin fiyatının 15 dakika içinde{" "}
            <strong>%3 yükseldiği</strong> an. Coin tekrar başladıkça satırı en
            üste çıkar.
          </li>
          <li>
            <strong>Noktalar ●●●○○:</strong> coinin diğerlerinden farklı
            olduğunu gösteren 5 işaretten kaçı var. <strong>Hacim</strong> (önce
            alım-satım arttı), <strong>Hızlandı</strong> (ilk saatte %5+),{" "}
            <strong>Tekrar</strong> (yine yükselişe geçti),{" "}
            <strong>Lider</strong> (en hareketli coin oldu),{" "}
            <strong>Çok sert</strong> (birden fırladı). Satıra dokununca
            hangileri var yazar. Renk: gri 1, mavi 2, turuncu 3, kırmızı 4–5.
          </li>
          <li>
            <strong>Listede</strong> 2 ve üstü noktası olanlar görünür. Yeniden
            başlayan veya yeni nokta alan coin en üste çıkar.{" "}
            <strong>4 saattir hareket etmeyen soluklaşır</strong>, 8 saati geçen
            en alttaki "N coin daha"ya iner (0–1 noktalılarla birlikte). Ölçüm:
            son 1 saatte hareket edenlerin 3'te 1'i günün en çok
            yükselenlerinden oluyor; 4 saattir sessiz olanlarda 8'de 1, 8
            saattir sessizlerde 14'te 1.
          </li>
          <li>
            <strong>Fiyat:</strong> coinin <strong>ilk ısındığı</strong> andaki
            fiyat → şimdi. <strong>Soldaki çizgi:</strong> yeşil = o andan beri
            yükselişte, kırmızı = düşüşte, çizgi yok = yerinde.
          </li>
          <li>
            <strong>Ölçüm (7 ay):</strong> ilk ısınan coinlerin 7'de 1'i günü
            Binance'in en çok yükselen 10 coini arasında bitiriyor; o
            yükselenlerin 10'da 8'i önce burada görünüyor. Nokta arttıkça
            ihtimal artıyor: 1 noktada 5'te 1, 2'de 3'te 1, 3'te 10'da 4, 4–5'te
            yarıdan fazlası.
          </li>
          <li>
            <strong>Kırmızı "+%40":</strong> coin ısındığında son 24 saatte
            zaten %40'tan fazla yükselmişti; çok sert iner çıkar.
          </li>
          <li>
            <strong>Unutma:</strong> bu bir "al" sinyali değil. Nokta arttıkça
            coin zaten yükselmiş oluyor; 4 saat sonrası her seviyede genelde
            eksi.
          </li>
        </ul>
      </details>
      {visible === null ? (
        <div className="signal-list">
          <div className="skeleton" />
        </div>
      ) : visible.length === 0 ? (
        <Empty
          title={`Son ${WARM_WINDOW_H} saatte ısınan coin yok`}
          detail="Bir coin bugün ilk kez 15 dakikada %3 yükseldiğinde burada görünecek."
        />
      ) : (
        <div className="table-shell" data-kind="quickmove">
          <div className="signal-head" aria-hidden="true">
            <span />
            <span>Sembol</span>
            <span>İlk ısınma</span>
            <span className="align-end">Başlangıç fiyatı</span>
            <span className="align-end">Şimdi</span>
          </div>
          <div className="signal-list">
            {(shown ?? []).map(({ last, history, first, moved }) => (
              <IgnitionItem
                key={last.symbol}
                last={last}
                history={history}
                first={first}
                quiet={first !== undefined && now - moved >= FADE_QUIET_MS}
                onSelect={onSelect}
              />
            ))}
          </div>
          {hiddenCount > 0 || showAll ? (
            <button
              type="button"
              className="link-btn qm-more"
              onClick={() => setShowAll((v) => !v)}
            >
              {showAll
                ? "Gizle ▴"
                : `${hiddenCount} coin daha (az noktalı veya 8 saattir sessiz) ▾`}
            </button>
          ) : null}
        </div>
      )}
    </>
  );
}

/** Satir alti: bugunse yalnizca saat, degilse tarih + saat. Acilan
 *  listede her zaman tam tarih + saat (kullanici istegi). */
function startTime(epochMs: number): string {
  const d = new Date(epochMs);
  return d.toDateString() === new Date().toDateString()
    ? hourMinute(epochMs)
    : dateTime(epochMs);
}

/** Son baslangic HARIC onceki baslangiclar + her birinin bir sonrakisi. */
function earlierRows(
  history: QuickMoveIgnition[],
): { h: QuickMoveIgnition; next: QuickMoveIgnition }[] {
  // En yeni ustte ("Eskiden yeniye" secenegi kaldirildi, kullanici 08.10).
  return history
    .slice(0, -1)
    .map((h, i) => ({ h, next: history[i + 1]! }))
    .reverse();
}

function IgnitionItem({
  last,
  history,
  first,
  quiet = false,
  onSelect,
}: {
  last: QuickMoveIgnition;
  history: QuickMoveIgnition[];
  /** Isinma gorunumu: coinin ilk isindigi an - fiyat ve degisim bundan olculur. */
  first?: QuickMoveIgnition;
  /** 4 saattir yeni hareket yok -> soluk (bkz. FADE_QUIET_MS). */
  quiet?: boolean;
  onSelect: (symbol: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const warm = first !== undefined;
  const base = first ?? last;
  const change =
    warm && last.price_now != null && base.price > 0
      ? (last.price_now / base.price - 1) * 100
      : last.change_pct;
  const earlier = history.length - 1;
  const signs = warm ? warmSigns(base, history) : null;
  const signCount = signs ? signs.filter((s) => s.on).length : 0;
  const side =
    !warm || change == null || Math.abs(change) < WARM_FLAT_PCT
      ? undefined
      : change > 0
        ? "BUY"
        : "SELL";
  return (
    <>
      <div
        className="signal"
        data-side={side}
        data-quiet={quiet ? "true" : undefined}
        title={
          quiet ? "4 saattir yeni hareket yok — pump ihtimali düştü" : undefined
        }
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="cell qm-avatar-cell">
          <CoinAvatar symbol={last.symbol} base={baseAsset(last.symbol)} />
        </span>
        <span className="signal-main">
          <span className="signal-symbol-row">
            <button
              type="button"
              className="signal-symbol"
              onClick={(event) => {
                event.stopPropagation();
                onSelect(last.symbol);
              }}
              aria-label={`${last.symbol} detayını aç`}
            >
              {last.symbol}
            </button>
            {signs ? (
              <SignBadge signs={signs} />
            ) : (last.nth_24h ?? 1) >= MANY_STARTS_FROM ? (
              <Pill
                tone="info"
                title="Günün 5. başlangıcı — çok tekrar, oynak: 4 saatte +%10 tepe %23 ama 3'te 2'si önce %5 düşüyor. 6. kez başlarsa listeden çıkar."
              >
                {last.nth_24h}. kez
              </Pill>
            ) : (last.nth_24h ?? 1) > 1 ? (
              <Pill
                tone="warn"
                title={`24 saatte ${last.nth_24h}. başlangıç — tekrar gelenler ilkinden daha sık devam ediyor`}
              >
                {last.nth_24h}. kez
              </Pill>
            ) : null}
            {risenAtTrigger(base) ? (
              <Pill
                tone="down"
                title={`Başlangıç anında 24 saatte ${pct(base.trigger_24h_pct ?? base.change_24h_pct, 0)}: 4 saatte +%10 tepe %44 ama %71'i önce −%5 görüyor (çok oynak)`}
              >
                +%40
              </Pill>
            ) : null}
          </span>
          <span className="signal-meta qm-start-meta">
            {warm ? "ilk " : ""}
            {startTime(base.close_time)} ·{" "}
            {ago(base.close_time).replace(/ önce$/, "")}
            {warm && earlier > 0 ? ` · son ${hourMinute(last.close_time)}` : ""}
            {earlier > 0 ? ` · ${earlier} ${warm ? "sonraki" : "önceki"}` : ""}
            {warm || earlier > 0 ? ` ${open ? "▴" : "▾"}` : ""}
          </span>
        </span>
        <span className="signal-numbers only-wide">
          {dateTime(base.close_time)}
        </span>
        <span className="signal-numbers align-end">
          <span className="signal-price">{price(base.price)}</span>
          <span className="signal-meta">
            {warm ? "ilk ısınma" : "başlangıç"}
          </span>
        </span>
        <span className="signal-numbers align-end">
          <span
            className={change == null ? undefined : change >= 0 ? "up" : "down"}
          >
            {pct(change)}
          </span>
          <span className="signal-meta">
            {last.price_now != null ? price(last.price_now) : "şimdi"}
          </span>
        </span>
      </div>
      {open ? (
        <div className="qm-expand">
          {signs ? (
            <>
              <ul className="qm-sign-list">
                {signs.map((s) => (
                  <li key={s.label} data-on={s.on ? "" : undefined}>
                    <span className="qm-sign-mark">{s.on ? "●" : "○"}</span>
                    <strong>{s.label}</strong>
                    <span>{s.detail}</span>
                  </li>
                ))}
              </ul>
              <p className="qm-sign-sum">
                <strong>5 işaretten {signCount} tanesi var.</strong> Geçmişte
                böyle coinlerin {SIGN_ODDS[signCount]} günü en çok yükselenler
                arasında bitirdi.
                <br />
                <span>
                  "Hemen al" demek değil — çoğu birkaç saat sonra biraz geri
                  düşüyor.
                </span>
              </p>
            </>
          ) : null}
          {earlier === 0 ? (
            <p className="note">Başka yükselişe geçmedi.</p>
          ) : (
            <>
              <p className="qm-starts-cap">
                Daha önce yükselişe geçtiği anlar · % = bir sonrakine kadar ne
                yaptı
              </p>
              <ul className="qm-starts-list">
                {earlierRows(history).map(({ h, next }, i, all) => {
                  // Son baslangic zaten satirda; burada yalnizca oncekiler,
                  // her biri bir SONRAKI baslangica kadar (kullanici: GTC ornegi).
                  // Sira her gun 00:00'da 1'den baslar -> gun basligi (kullanici,
                  // 08.10: "2, 1, 4, 3, 2, 1, 1" sacma gorunuyordu).
                  const move = (next.price / h.price - 1) * 100;
                  const newDay =
                    i === 0 ||
                    trDay(all[i - 1]!.h.close_time) !== trDay(h.close_time);
                  return (
                    <Fragment key={h.close_time}>
                      {newDay ? (
                        <li className="qm-starts-day">
                          {dateTime(h.close_time).split(" ")[0]}
                        </li>
                      ) : null}
                      <li>
                        <span className="qm-starts-nth">{h.nth_24h ?? 1}.</span>
                        <span className="qm-starts-time">
                          {hourMinute(h.close_time)}
                        </span>
                        <span className="qm-starts-price">
                          {price(h.price)}
                        </span>
                        <span
                          className={`qm-starts-move ${move >= 0 ? "up" : "down"}`}
                        >
                          {pct(move)}
                        </span>
                      </li>
                    </Fragment>
                  );
                })}
              </ul>
            </>
          )}
        </div>
      ) : null}
    </>
  );
}

function QuickMoveItem({
  row,
  rank,
  onSelect,
  triggers,
  signs,
}: {
  row: QuickMoveRow;
  rank: number;
  onSelect: (symbol: string) => void;
  /** En yeni basta gelir. */
  triggers: QuickMoveHistoryRow[];
  /** Coin Baslayanlar'daysa nokta rozeti (bkz. warmSigns). */
  signs?: Sign[];
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <div
        className="signal"
        data-above={row.above_threshold ? "true" : undefined}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="cell qm-avatar-cell">
          <CoinAvatar symbol={row.symbol} base={baseAsset(row.symbol)} />
        </span>

        <span className="signal-main">
          <span className="signal-symbol-row">
            <button
              type="button"
              className="signal-symbol"
              onClick={(event) => {
                event.stopPropagation();
                onSelect(row.symbol);
              }}
              aria-label={`${row.symbol} detayını aç`}
            >
              {row.symbol}
            </button>
            {signs ? <SignBadge signs={signs} /> : null}
            {row.score >= 10 ? (
              <Pill
                tone="warn"
                title="Skor 10+ — ölçümde 1 saat içinde %3 hareket 20'de 19 (yönü yazı tura)"
              >
                Çok güçlü
              </Pill>
            ) : row.score >= 6.5 ? (
              <Pill
                tone="info"
                title="Skor 6,5+ — ölçüm (40 gün): 1 saat içinde %3 oynama 10'da 9, ama önce yukarı %43 / önce aşağı %45 (yazı tura)"
              >
                Güçlü
              </Pill>
            ) : null}
            {risenTooMuch(row) ? (
              <Pill
                tone="warn"
                title="24 saatte +%40'ın üstünde — bu coinlerin 3'te 2'si sonraki 24 saatte −%15 görüyor"
              >
                24s {pct(row.change_24h_pct, 0)}
              </Pill>
            ) : null}
          </span>
          <span
            className="signal-meta qm-start-meta"
            title="Kaç kez: skor son 48 saatte kaç kez 6,5'i geçti (6 ve üstü: kazanç artmıyor, −%15 riski %20'yi aşıyor). Satıra bas: o anları gör."
          >
            #{rank} · {price(row.price)}
            {triggers.length > 0
              ? ` · ${triggers.length} kez ${open ? "▴" : "▾"}`
              : ""}
          </span>
        </span>

        <span
          className="signal-numbers align-end only-narrow"
          title="Son 3 dakikadaki fiyat değişimi ve skor"
        >
          <span className={row.velocity >= 0 ? "up" : "down"}>
            {pct(row.velocity, 1)}
          </span>
          <span className="signal-meta">
            skor {row.score.toFixed(1).replace(".", ",")}
          </span>
        </span>

        <span className="signal-numbers only-wide">
          <span className={row.velocity >= 0 ? "up" : "down"}>
            {pct(row.velocity, 3)}
          </span>
        </span>

        <span className="signal-numbers only-wide">
          <span className={row.acceleration >= 0 ? "up" : "down"}>
            {row.acceleration >= 0 ? "+" : ""}
            {row.acceleration.toFixed(3)} puan
          </span>
        </span>

        <span className="signal-numbers align-end only-wide">
          <span className="signal-price">{row.score.toFixed(3)}</span>
        </span>
      </div>
      {open ? (
        <div className="qm-expand">
          {triggers.length === 0 ? (
            <p className="note">
              Son 48 saatte güçlü tetiklenmesi yok — bu ilk.
            </p>
          ) : (
            <>
              <p className="qm-starts-cap">
                Son 48 saatte skorun 6,5'i geçtiği anlar
              </p>
              <ul className="qm-starts-list qm-trig-list">
                {triggers.map((t) => (
                  <li key={t.close_time}>
                    <span className="qm-starts-time">
                      {dateTime(t.close_time)}
                    </span>
                    <span className="qm-starts-price">{price(t.price)}</span>
                    <span className="qm-starts-move">
                      skor {t.score.toFixed(1).replace(".", ",")}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      ) : null}
    </>
  );
}
